'use client';
import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { FIXTURE_PRESETS, estimateLumensFromWatts } from '@/lighting-app/data/lightingStandards';
import type { DesignWizardState } from './DesignWizardClient';

interface StepFixtureProps {
  state: DesignWizardState;
  onChange: (updates: Partial<DesignWizardState>) => void;
  onBack: () => void;
  onCalculate: () => void;
}

const COLOR_TEMPS = [
  { value: '2700', label: '2700K', desc: 'دافئ جداً', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: '3000', label: '3000K', desc: 'دافئ', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: '4000', label: '4000K', desc: 'طبيعي', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: '5000', label: '5000K', desc: 'أبيض', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: '6500', label: '6500K', desc: 'بارد', color: 'bg-blue-100 text-blue-800 border-blue-300' },
];

export default function StepFixture({ state, onChange, onBack, onCalculate }: StepFixtureProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isManual = state.fixtureId === 'manual';

  const handlePresetSelect = (id: string) => {
    const preset = FIXTURE_PRESETS.find((f) => f.id === id);
    if (!preset) return;
    if (id === 'manual') {
      onChange({ fixtureId: 'manual', fixtureLumens: '', fixtureWatts: '', beamAngle: '120' });
    } else {
      onChange({
        fixtureId: id,
        fixtureLumens: String(preset.lumens),
        fixtureWatts: String(preset.watts),
        beamAngle: String(preset.beamAngle),
      });
    }
  };

  const handleWattsChange = (watts: string) => {
    onChange({ fixtureWatts: watts });
    if (state.fixtureId && state.fixtureId !== 'manual') {
      const estimated = estimateLumensFromWatts(parseFloat(watts) || 0, state.fixtureId);
      onChange({ fixtureWatts: watts, fixtureLumens: String(estimated) });
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!state.fixtureId) errs.fixture = 'اختر نوع المصباح';
    if (!state.fixtureLumens || parseFloat(state.fixtureLumens) <= 0) errs.lumens = 'أدخل قيمة Lumen صحيحة';
    if (!state.fixtureWatts || parseFloat(state.fixtureWatts) <= 0) errs.watts = 'أدخل قيمة Watt صحيحة';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCalculate = () => {
    if (validate()) onCalculate();
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border-2 bg-card text-foreground font-tabular text-base transition-all duration-200 focus:outline-none focus:border-primary ${
      errors[field] ? 'border-danger' : 'border-border'
    }`;

  return (
    <div className="slide-up">
      <div className="text-center mb-8">
        <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
          ما نوع المصابيح التي ستستخدمها؟
        </h2>
        <p className="text-muted-foreground">اختر من القائمة أو أدخل بيانات مصباحك يدوياً</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Fixture Presets */}
        <div className="wizard-card p-6">
          <h3 className="font-bold text-foreground mb-4">اختر نوع المصباح</h3>
          {errors.fixture && <p className="text-danger text-sm mb-3">{errors.fixture}</p>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {FIXTURE_PRESETS.map((fixture) => (
              <button
                key={`fixture-${fixture.id}`}
                onClick={() => handlePresetSelect(fixture.id)}
                className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                  state.fixtureId === fixture.id
                    ? 'border-primary bg-accent' :'border-border bg-card hover:border-primary/40 hover:bg-accent/30'
                }`}
              >
                <div className="text-2xl mb-1">{fixture.icon}</div>
                <div className="text-xs font-bold text-foreground">{fixture.nameAr}</div>
                {fixture.id !== 'manual' && (
                  <div className="text-xs text-muted-foreground font-tabular mt-0.5">{fixture.lumens} lm</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Fixture Data */}
        {state.fixtureId && (
          <div className="wizard-card p-6 fade-in">
            <h3 className="font-bold text-foreground mb-4">
              {isManual ? 'أدخل بيانات المصباح' : 'بيانات المصباح'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Lumen لكل مصباح <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">شدة الضوء المنبعث</p>
                <input
                  type="number"
                  min="1"
                  placeholder="مثال: 1200"
                  value={state.fixtureLumens}
                  onChange={(e) => onChange({ fixtureLumens: e.target.value })}
                  readOnly={!isManual}
                  className={`${inputClass('lumens')} ${!isManual ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {errors.lumens && <p className="text-danger text-xs mt-1">{errors.lumens}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Watt لكل مصباح <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">استهلاك الطاقة</p>
                <input
                  type="number"
                  min="1"
                  placeholder="مثال: 12"
                  value={state.fixtureWatts}
                  onChange={(e) => handleWattsChange(e.target.value)}
                  readOnly={!isManual}
                  className={`${inputClass('watts')} ${!isManual ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {errors.watts && <p className="text-danger text-xs mt-1">{errors.watts}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  زاوية الانتشار (Beam Angle)
                </label>
                <p className="text-xs text-muted-foreground mb-2">بالدرجة — اختياري</p>
                <input
                  type="number"
                  min="10"
                  max="360"
                  placeholder="120"
                  value={state.beamAngle}
                  onChange={(e) => onChange({ beamAngle: e.target.value })}
                  className={inputClass('beamAngle')}
                />
              </div>
            </div>

            {/* Color Temperature */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-foreground mb-2">
                درجة حرارة اللون (Kelvin) — اختياري
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_TEMPS.map((ct) => (
                  <button
                    key={`ct-${ct.value}`}
                    onClick={() => onChange({ colorTemp: ct.value })}
                    className={`px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-all duration-200 ${
                      state.colorTemp === ct.value
                        ? ct.color + 'border-current' :'border-border text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <div>{ct.label}</div>
                    <div className="opacity-70">{ct.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Advanced Settings */}
        <div className="wizard-card overflow-hidden">
          <button
            onClick={() => onChange({ useAdvanced: !state.useAdvanced })}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info size={16} className="text-primary" />
              <span className="font-semibold text-foreground">إعدادات متقدمة</span>
              <span className="text-xs text-muted-foreground">(للمهندسين)</span>
            </div>
            {state.useAdvanced ? <ChevronUp size={20} className="text-muted-foreground" /> : <ChevronDown size={20} className="text-muted-foreground" />}
          </button>

          {state.useAdvanced && (
            <div className="px-6 pb-6 fade-in border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Utilization Factor (UF)
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">عامل الاستفادة — القيمة الافتراضية: 0.65</p>
                  <input
                    type="number"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={state.uf}
                    onChange={(e) => onChange({ uf: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground font-tabular focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Maintenance Factor (MF)
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">عامل الصيانة — القيمة الافتراضية: 0.80</p>
                  <input
                    type="number"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={state.mf}
                    onChange={(e) => onChange({ mf: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground font-tabular focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 max-w-2xl mx-auto">
        <button onClick={onBack} className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
          <ArrowRight size={18} />
          <span>السابق</span>
        </button>
        <button
          onClick={handleCalculate}
          className="btn-primary px-8 py-3 rounded-xl flex items-center gap-2"
        >
          <span>احسب الإضاءة</span>
          <ArrowLeft size={18} />
        </button>
      </div>
    </div>
  );
}